const DEFAULT_REQUIRED_DOCUMENTS = [
  "government_id",
  "professional_license",
  "tb_test",
  "cpr_bls",
];

function norm(v) {
  return String(v || "").trim().toLowerCase();
}

function toMillis(v) {
  if (!v) return NaN;
  try {
    if (typeof v?.toDate === "function") return v.toDate().getTime();
    if (typeof v === "number") return v;
    return new Date(v).getTime();
  } catch {
    return NaN;
  }
}

function getRequiredDocuments(worker = {}) {
  const role = norm(worker.role || worker.platformRole);

  if (role === "worker" || role === "nurse" || role === "caregiver") {
    return DEFAULT_REQUIRED_DOCUMENTS;
  }

  if (
    role === "facility_planner" ||
    role === "facility_admin_viewer" ||
    role === "operations_admin" ||
    role === "finance_admin"
  ) {
    return ["government_id"];
  }

  return DEFAULT_REQUIRED_DOCUMENTS;
}

export default function getWorkerCompliance(worker = {}) {
  const documents = worker.credentialMap || worker.documents || worker.uploadedDocs || {};
  const requiredDocs = getRequiredDocuments(worker);

  const onboardingStatus = norm(worker.onboardingStatus);
  const invitationStatus = norm(worker.invitationStatus);
  const status = norm(worker.status);

  const approvedDocs = [];
  const missingDocs = [];
  const pendingDocs = [];
  const rejectedDocs = [];
  const expiredDocs = [];

  requiredDocs.forEach((key) => {
    const doc = documents?.[key];

    if (!doc) {
      missingDocs.push(key);
      return;
    }

    const docStatus = norm(doc.status);

    if (docStatus === "approved") {
      approvedDocs.push(key);
    } else if (docStatus === "pending_review" || docStatus === "submitted") {
      pendingDocs.push(key);
    } else if (
      docStatus === "reupload_required" ||
      docStatus === "rejected" ||
      docStatus === "failed"
    ) {
      rejectedDocs.push(key);
    } else {
      missingDocs.push(key);
    }

    const expMs = toMillis(doc.expiresAt || doc.expirationDate);
    if (Number.isFinite(expMs) && expMs < Date.now()) {
      expiredDocs.push(key);
    }
  });

  const docsApproved =
    approvedDocs.length === requiredDocs.length &&
    missingDocs.length === 0 &&
    pendingDocs.length === 0 &&
    rejectedDocs.length === 0 &&
    expiredDocs.length === 0;

  const inviteApproved =
    invitationStatus === "approved" ||
    invitationStatus === "" ||
    invitationStatus === "not_submitted";

  const onboardingApproved =
    onboardingStatus === "approved" ||
    worker.onboardingCompleted === true;

  const active = status === "active";
const operationalReady =
  String(worker.workerOperationalState || "").toUpperCase() === "READY" ||
  String(worker.complianceStatus || "").toLowerCase() === "ready";

const canWork =
  docsApproved &&
  inviteApproved &&
  onboardingApproved &&
  active &&
  operationalReady;

  let label = "NOT READY";
  let reason = "Worker is not fully approved.";

  if (canWork) {
    label = "READY";
    reason = "Worker is fully compliant and eligible for shifts.";
  } else if (!inviteApproved) {
    label = "INVITE PENDING";
    reason = "Admin must approve the invite first.";
  } else if (rejectedDocs.length > 0) {
    label = "ACTION REQUIRED";
    reason = "Worker must reupload rejected documents.";
  } else if (pendingDocs.length > 0 || onboardingStatus === "submitted") {
    label = "UNDER REVIEW";
    reason = "Documents are waiting for admin review.";
  } else if (missingDocs.length > 0) {
    label = "MISSING DOCUMENTS";
    reason = "Worker must upload required documents.";
  } else if (expiredDocs.length > 0) {
    label = "EXPIRED DOCUMENTS";
    reason = "One or more required documents have expired.";
  } else if (!onboardingApproved) {
    label = "ONBOARDING NOT APPROVED";
    reason = "Admin must approve onboarding.";
  } else if (!active) {
  label = "INACTIVE";
  reason = "Worker account is not active.";
} else if (!operationalReady) {
  label = "NOT READY";
  reason = "Worker has not been released for shift operations.";
}

  return {
    requiredDocs,
    approvedDocs,
    missingDocs,
    pendingDocs,
    rejectedDocs,
    expiredDocs,

    docsApproved,
    inviteApproved,
    onboardingApproved,
    active,
    operationalReady,

    canWork,
    blocked: !canWork,

    label,
    reason,
  };
}